import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'

import {
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  FolderIcon,
  MoreIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  ShareIcon,
  TemporaryChatIcon,
  TrashIcon,
} from '../components/icons'
import { ThemeToggle } from '../components/ThemeToggle'
import { LanguagePicker } from '../i18n/LanguagePicker'
import {
  type Conversation,
  type MessageSearchHit,
  type Project,
  PROJECTS_CHANGED_EVENT,
  TEMPORARY_CHANGED_EVENT,
  closeTemporaryConversation,
  createProject,
  deleteConversation,
  getTemporaryConversation,
  listConversations,
  listProjectConversations,
  listProjects,
  moveConversation,
  renameConversation,
  searchConversationTitles,
  searchMessages,
} from '../lib/conversations'
import { ApiError } from '../lib/api'
import { TERMINAL_STATUSES, useStreams } from '../stream/StreamManager'
import { DeleteProjectDialog } from './DeleteProjectDialog'
import { ShareDialog } from './ShareDialog'

const SIDEBAR_MIN = 200
const SIDEBAR_MAX = 480
const SIDEBAR_DEFAULT = 280
const STORAGE_KEY = 'sidebar-width'
// Her tusta iki sorgu atmamak icin (bkz. CommandPalette).
const SEARCH_DEBOUNCE_MS = 200

function clampWidth(w: number) {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, w))
}

/**
 * Konusma listesi.
 *
 * ## Keyset pagination, offset DEGIL
 *
 * Sunucu `?before=<last_message_at>` aliyor. Offset'te es zamanli bir yeni
 * mesaj tum listeyi kaydiriyor ve kullanici ayni konusmayi iki kez veya HIC
 * gormuyor. Keyset'te boyle bir kayma yok.
 *
 * ## Uretim yapan konusma isaretleniyor
 *
 * Sidebar `useStreams()`ten okuyor, kendi state'i YOK. Kullanici baska bir
 * sohbete gecip donebiliyor ve isaret hala dogru - cunku akisin sahibi
 * `<StreamManager>` ve gezinme onu etkilemiyor.
 *
 * ## Yeniden boyutlandirma (resize)
 *
 * Sag kenara tutturulmus bir drag handle ile genislik degistirilir.
 * Min: 200px, Max: 480px. Tercih localStorage'a kaydedilir.
 */
export function Sidebar({ onNavigate }: { onNavigate?(): void }) {
  const { t } = useTranslation()
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const { runs } = useStreams()
  const [items, setItems] = useState<Conversation[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const sentinel = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const search = useSidebarSearch(query)
  const removeFromSearch = search.remove
  const updateInSearch = search.update
  const searching = query.trim().length > 0
  const [projects, setProjects] = useState<Project[]>([])
  // Artinca acik projenin sohbet listesi yeniden cekiliyor (tasima, yeni sohbet).
  const [projectsVersion, setProjectsVersion] = useState(0)

  // Sidebar genisligi
  const [width, setWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = parseInt(saved, 10)
      if (!isNaN(parsed)) return clampWidth(parsed)
    }
    return SIDEBAR_DEFAULT
  })

  // Resize drag logic
  const dragRef = useRef<{ startX: number; startW: number } | null>(null)

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      dragRef.current = { startX: e.clientX, startW: width }

      function onMouseMove(ev: MouseEvent) {
        if (!dragRef.current) return
        const delta = ev.clientX - dragRef.current.startX
        const newW = clampWidth(dragRef.current.startW + delta)
        setWidth(newW)
      }

      function onMouseUp() {
        if (dragRef.current) {
          const finalW = clampWidth(dragRef.current.startW + 0) // just flush
          localStorage.setItem(STORAGE_KEY, String(finalW))
        }
        dragRef.current = null
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }

      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    },
    [width],
  )

  // Width'i CSS var olarak uygula
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', `${width}px`)
    localStorage.setItem(STORAGE_KEY, String(width))
  }, [width])

  // `t`yi ref uzerinden: `load`un bagimliligi olmasi, dil degistiginde tum
  // listeyi bastan cekmek demek olurdu (bkz. `ConversationView`).
  const tRef = useRef(t)
  tRef.current = t

  const load = useCallback(async (before: string | null) => {
    setError(null)
    try {
      const page = await listConversations(before)
      setItems((current) => {
        // Refreshing the first page replaces stale titles with the latest
        // server values, including asynchronously generated AI titles.
        if (before === null) {
          const freshIds = new Set(page.items.map((item) => item.id))
          return [...page.items, ...current.filter((item) => !freshIds.has(item.id))]
        }
        const seen = new Set(current.map((item) => item.id))
        return [...current, ...page.items.filter((item) => !seen.has(item.id))]
      })
      setCursor(page.next_cursor)
    } catch {
      setError(tRef.current('chat.sidebar.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(null)
  }, [load])

  // AI-generated titles complete after the main response, so refresh the
  // first list page periodically without interrupting the conversation.
  useEffect(() => {
    const interval = window.setInterval(() => void load(null), 15_000)
    return () => window.clearInterval(interval)
  }, [load])

  // Sonsuz kaydirma: sentinel gorununce sonraki sayfa.
  //
  // `IntersectionObserver`, scroll dinleyicisi DEGIL: scroll handler'i
  // saniyede onlarca kez tetikleniyor ve her seferinde layout okumasi
  // yapiyor (`scrollHeight`), yani jank uretiyor.
  useEffect(() => {
    const node = sentinel.current
    if (!node || cursor === null) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) void load(cursor)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [cursor, load])

  // TERMINAL run'lar sayilmiyor. `StreamManager` bitmis run'lari listede
  // TUTUYOR (kalici mesaj gelene kadar metin ekranda kalsin diye), yani
  // filtresiz bir `runs` okumasi tamamlanmis bir sohbette de nokta yanip
  // sondurmeye devam ederdi.
  const streamingIds = new Set(
    runs.filter((run) => !TERMINAL_STATUSES.has(run.status)).map((run) => run.conversation_id),
  )

  const handleRename = useCallback(async (id: string, title: string) => {
    const updated = await renameConversation(id, title)
    setItems((current) => current.map((item) => (item.id === id ? updated : item)))
  }, [])

  const reloadProjects = useCallback(async () => {
    try {
      const page = await listProjects()
      setProjects(Array.isArray(page?.items) ? page.items : [])
    } catch {
      // Projeler ikincil bilgi: yuklenemezse bolum bos kaliyor, sohbetler etkilenmiyor.
    }
    setProjectsVersion((value) => value + 1)
  }, [])

  useEffect(() => {
    void reloadProjects()
    const onChanged = () => void reloadProjects()
    window.addEventListener(PROJECTS_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(PROJECTS_CHANGED_EVENT, onChanged)
  }, [reloadProjects])

  // Sohbeti projeye tasi (`null` = projeden cikar). Siradan liste projeli
  // sohbetleri icermiyor: tasinan cikiyor, projeden cikan ilk sayfayla geri geliyor.
  const handleMove = useCallback(
    async (id: string, projectId: string | null) => {
      const updated = await moveConversation(id, projectId)
      if (projectId) setItems((current) => current.filter((item) => item.id !== id))
      else void load(null)
      updateInSearch(updated)
      await reloadProjects()
    },
    [load, reloadProjects, updateInSearch],
  )

  // --- gecici sohbet ---------------------------------------------------
  //
  // Gecmis listesinde YOK (sunucu onu hicbir listede dondurmuyor), bu yuzden
  // ayrica soruluyor ve KENDI satirinda gosteriliyor. Boyle olmasaydi
  // kullanici acik bir gecici sohbete geri donemez ve onu kapatamazdi -
  // kapatma, icerigin silinmesinin TEK yolu.
  const [temporaryChat, setTemporaryChat] = useState<Conversation | null>(null)

  const reloadTemporary = useCallback(async () => {
    try {
      setTemporaryChat(await getTemporaryConversation())
    } catch {
      // Ikincil bilgi: alinamazsa satir gorunmuyor, gecmis etkilenmiyor.
    }
  }, [])

  useEffect(() => {
    void reloadTemporary()
    const onChanged = () => void reloadTemporary()
    window.addEventListener(TEMPORARY_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(TEMPORARY_CHANGED_EVENT, onChanged)
    // `conversationId`: ilk mesajdan sonra gecici sohbet SUNUCUDA olusuyor;
    // o ana kadar gosterilecek bir satir yok.
  }, [reloadTemporary, conversationId])

  const startTemporary = useCallback(() => {
    // Ayni anda tek gecici sohbet: acik olan varsa yenisini acmak yerine
    // ONA gidiyoruz. Aksi halde kullanici farkinda olmadan birden fazla
    // gecici sohbet biriktirir ve hepsini tek tek kapatmasi gerekirdi.
    navigate(temporaryChat ? `/c/${temporaryChat.id}` : '/?temporary=1')
    onNavigate?.()
  }, [navigate, onNavigate, temporaryChat])

  const handleCloseTemporary = useCallback(
    async (id: string) => {
      await closeTemporaryConversation(id)
      setTemporaryChat(null)
      if (id === conversationId) navigate('/')
    },
    [conversationId, navigate],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteConversation(id)
      setItems((current) => current.filter((item) => item.id !== id))
      removeFromSearch(id)
      if (id === conversationId) navigate('/')
    },
    [conversationId, navigate, removeFromSearch],
  )

  return (
    <nav className="sidebar" aria-label={t('chat.sidebar.label')} style={{ width }}>
      {/* Resize handle — sag kenarda, yalnizca masaustu */}
      <div
        className="sidebar-resize-handle"
        onMouseDown={onHandleMouseDown}
        aria-hidden="true"
        title={t('chat.sidebar.resizeHandle')}
      />

      <button
        type="button"
        className="btn-new"
        onClick={() => {
          navigate('/')
          onNavigate?.()
        }}
      >
        + {t('chat.sidebar.newChat')}
      </button>

      <button type="button" className="btn-temporary" onClick={startTemporary}>
        <TemporaryChatIcon size={15} />
        {t('chat.sidebar.temporaryChat')}
      </button>

      {temporaryChat && (
        <div
          className={`temporary-row${temporaryChat.id === conversationId ? ' active' : ''}`}
        >
          <Link
            to={`/c/${temporaryChat.id}`}
            className="temporary-link"
            onClick={() => onNavigate?.()}
          >
            <TemporaryChatIcon size={14} />
            <span>{t('chat.sidebar.temporaryOpen')}</span>
          </Link>
          <button
            type="button"
            className="temporary-close"
            onClick={() => void handleCloseTemporary(temporaryChat.id)}
            aria-label={t('chat.sidebar.temporaryClose')}
            title={t('chat.sidebar.temporaryClose')}
          >
            <TrashIcon size={14} />
          </button>
        </div>
      )}

      {/*
        Kenar cubugu aramasi: baslik (tum sohbetler) + mesaj icerigi. Ctrl+K
        paleti ayni isi yapiyor ama gorunur degildi - kullanici listeyi
        gorurken aramak istiyor.
      */}
      <div className="sidebar-search" role="search">
        <SearchIcon size={15} />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && query) {
              event.preventDefault()
              event.stopPropagation()
              setQuery('')
            }
          }}
          placeholder={t('chat.sidebar.search.placeholder')}
          aria-label={t('chat.sidebar.search.label')}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            className="sidebar-search-clear"
            onClick={() => setQuery('')}
            aria-label={t('chat.sidebar.search.clear')}
          >
            <CloseIcon size={14} />
          </button>
        )}
      </div>

      {/*
        Scroll YALNIZCA bu sarmalayicida: `.sidebar`in kendisi artik
        kaymiyor, aksi halde marka basligi, "+ Yeni sohbet" ve alttaki
        tema/dil bloğu listeyle birlikte ekrandan kaybolurdu. Sentinel de
        BURADA (ul'nin disinda ama scroll bolgesinin icinde) olmak zorunda -
        disarida olsaydi her zaman gorunur kalir ve sonsuz kaydirma anlik
        tetiklenirdi.
      */}
      <div className="sidebar-scroll">
        {searching ? (
          <SearchResults
            titles={search.titles}
            hits={search.hits}
            pending={search.pending}
            conversationId={conversationId}
            streamingIds={streamingIds}
            onNavigate={onNavigate}
            onRename={handleRename}
            onDelete={handleDelete}
            projects={projects}
            onMove={handleMove}
          />
        ) : (
          <>
            <ProjectsSection
              projects={projects}
              version={projectsVersion}
              onCreated={reloadProjects}
              onMove={handleMove}
              conversationId={conversationId}
              streamingIds={streamingIds}
              onNavigate={onNavigate}
            />
            <ul className="conv-list">
              {items.map((conversation) => (
                <ConvItem
                  key={conversation.id}
                  conversation={conversation}
                  active={conversation.id === conversationId}
                  generating={streamingIds.has(conversation.id)}
                  onNavigate={onNavigate}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  projects={projects}
                  onMove={handleMove}
                />
              ))}
            </ul>

            {loading && <p className="sidebar-note">{t('common.loading')}</p>}
            {error && (
              <p className="sidebar-note" role="alert">
                {error}
              </p>
            )}
            {!loading && items.length === 0 && !error && (
              <p className="sidebar-note">{t('chat.sidebar.empty')}</p>
            )}
            {/* Sonsuz kaydirma sentineli: yalnizca daha sayfa varsa. */}
            {cursor !== null && <div ref={sentinel} className="sentinel" aria-hidden="true" />}
          </>
        )}
      </div>

      {/*
        Tema + dil kontrolu - HER SAYFADA AYNI yerde, ayni bicimde (bkz.
        App.tsx ThemeProvider yorumu). Kullanicinin "tasarimlarda tema/dil
        farkli farkli olmus ama projede tek tip olmali" gerekcesi burada
        uygulaniyor: bu blok sidebar'in oldugu her ekranda ayni.
      */}
      <div className="sidebar-footer">
        <ThemeToggle variant="compact" showLabel />
        <LanguagePicker persist variant="compact" />
      </div>
    </nav>
  )
}

interface SidebarSearchState {
  titles: Conversation[]
  hits: MessageSearchHit[]
  pending: boolean
  remove(id: string): void
  update(conversation: Conversation): void
}

/**
 * Gecikmeli iki arama: basliklar ve mesaj icerigi. Eski bir yanitin yeni
 * sorgunun sonucunu ezmemesi icin her istek bir sira numarasi tasiyor.
 */
function useSidebarSearch(query: string): SidebarSearchState {
  const [titles, setTitles] = useState<Conversation[]>([])
  const [hits, setHits] = useState<MessageSearchHit[]>([])
  const [pending, setPending] = useState(false)
  const latest = useRef(0)

  useEffect(() => {
    const trimmed = query.trim()
    const request = ++latest.current
    if (!trimmed) {
      setTitles([])
      setHits([])
      setPending(false)
      return
    }
    setPending(true)
    const timer = window.setTimeout(() => {
      void Promise.all([
        searchConversationTitles(trimmed)
          .then((page) => page.items)
          .catch(() => [] as Conversation[]),
        // 503 = icerik aramasi kapali; baslik sonuclari yine gosterilir.
        trimmed.length < 2
          ? Promise.resolve([] as MessageSearchHit[])
          : searchMessages(trimmed)
              .then((data) => data.items)
              .catch(() => [] as MessageSearchHit[]),
      ]).then(([nextTitles, nextHits]) => {
        if (request !== latest.current) return
        setTitles(nextTitles)
        setHits(nextHits)
        setPending(false)
      })
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query])

  const remove = useCallback((id: string) => {
    setTitles((current) => current.filter((item) => item.id !== id))
    setHits((current) => current.filter((hit) => hit.conversation_id !== id))
  }, [])

  const update = useCallback((conversation: Conversation) => {
    setTitles((current) =>
      current.map((item) => (item.id === conversation.id ? conversation : item)),
    )
  }, [])

  return { titles, hits, pending, remove, update }
}

function SearchResults({
  titles,
  hits,
  pending,
  conversationId,
  streamingIds,
  onNavigate,
  onRename,
  onDelete,
  projects,
  onMove,
}: {
  titles: Conversation[]
  hits: MessageSearchHit[]
  pending: boolean
  conversationId: string | undefined
  streamingIds: Set<string>
  onNavigate?(): void
  onRename(id: string, title: string): Promise<void>
  onDelete(id: string): Promise<void>
  projects: Project[]
  onMove(id: string, projectId: string | null): Promise<void>
}) {
  const { t } = useTranslation()
  // Icerik eslesmeleri SOHBET basina bir satir: ayni sohbette on mesaj
  // eslesirse listeyi ayni baslikla doldurmak yerine ilk (en iyi) kesit.
  const titleIds = new Set(titles.map((item) => item.id))
  const seen = new Set<string>()
  const contentHits = hits.filter((hit) => {
    if (titleIds.has(hit.conversation_id) || seen.has(hit.conversation_id)) return false
    seen.add(hit.conversation_id)
    return true
  })

  if (titles.length === 0 && contentHits.length === 0) {
    return (
      <p className="sidebar-note" role="status">
        {pending ? t('chat.sidebar.search.searching') : t('chat.sidebar.search.noResults')}
      </p>
    )
  }

  return (
    <>
      {titles.length > 0 && (
        <>
          <p className="sidebar-label">{t('chat.sidebar.search.titles')}</p>
          <ul className="conv-list">
            {titles.map((conversation) => (
              <ConvItem
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === conversationId}
                generating={streamingIds.has(conversation.id)}
                onNavigate={onNavigate}
                onRename={onRename}
                onDelete={onDelete}
                projects={projects}
                onMove={onMove}
              />
            ))}
          </ul>
        </>
      )}
      {contentHits.length > 0 && (
        <>
          <p className="sidebar-label">{t('chat.sidebar.search.messages')}</p>
          <ul className="conv-list">
            {contentHits.map((hit) => (
              <li key={hit.message_id} className="conv-item">
                <Link
                  to={`/c/${hit.conversation_id}`}
                  className={
                    hit.conversation_id === conversationId
                      ? 'conv conv-hit active'
                      : 'conv conv-hit'
                  }
                  onClick={onNavigate}
                >
                  <span className="conv-title">{hit.conversation_title || t('chat.untitled')}</span>
                  <span className="conv-snippet">{stripMarks(hit.snippet)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/** FTS `snippet()` isaretleri (`[[kelime]]`) ve olasi HTML etiketleri - bkz. CommandPalette. */
function stripMarks(snippet: string): string {
  return snippet.replace(/\[\[|\]\]/g, '').replace(/<\/?[a-z][^>]*>/gi, '')
}

/**
 * Tek konusma satiri: link + hover'da beliren "⋯" menusu (yeniden
 * adlandir / sil). Silme geri alinamaz gorunumu yuzunden native
 * `confirm()` yerine popover icinde inline onay adimi kullaniyor.
 */
/**
 * Projeler: sohbetleri gruplayan basliklar.
 *
 * Iki kaynak: API ile gelen platform istekleri (kullanici karari, 22 Eylul -
 * istekler suresiz saklanip proje basligi altinda gorunecek) ve kullanicinin
 * "+" ile actigi projeler. Proje icinde "Yeni sohbet" `/?project=<id>`e
 * gidiyor; ilk mesaj sohbeti o projede aciyor. Mevcut sohbetler "⋯" menusundeki
 * "Projeye tasi" ile tasiniyor.
 *
 * Siradan sohbet listesi projeli sohbetleri ICERMIYOR (gunde yuzlerce istek
 * listeyi dolduracakti). Proje satirina tiklaninca o projenin sohbetleri
 * yukleniyor; fazlasi "Daha fazla" ile.
 */
function ProjectsSection({
  projects,
  version,
  onCreated,
  onMove,
  conversationId,
  streamingIds,
  onNavigate,
}: {
  projects: Project[]
  version: number
  onCreated(): Promise<void>
  onMove(id: string, projectId: string | null): Promise<void>
  conversationId: string | undefined
  streamingIds: Set<string>
  onNavigate?(): void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [openId, setOpenId] = useState<string | null>(null)
  const [items, setItems] = useState<Conversation[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  // Silinmek uzere secilen proje: pencere iki secenek gosteriyor (bkz.
  // `DeleteProjectDialog`).
  const [deleting, setDeleting] = useState<Project | null>(null)

  const load = useCallback(async (projectId: string, before: string | null) => {
    setLoading(true)
    try {
      const page = await listProjectConversations(projectId, before)
      const received = Array.isArray(page?.items) ? page.items : []
      setItems((current) => (before ? [...current, ...received] : received))
      setCursor(page?.next_cursor ?? null)
    } catch {
      setCursor(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Tasima / proje icinde yeni sohbet: acik projenin listesi tazeleniyor.
  const openRef = useRef(openId)
  openRef.current = openId
  useEffect(() => {
    if (version > 0 && openRef.current) void load(openRef.current, null)
  }, [version, load])

  function open(projectId: string) {
    setOpenId(projectId)
    setItems([])
    setCursor(null)
    void load(projectId, null)
  }

  function toggle(projectId: string) {
    if (openId === projectId) setOpenId(null)
    else open(projectId)
  }

  async function create(name: string): Promise<string | null> {
    try {
      const project = await createProject(name)
      await onCreated()
      setCreating(false)
      open(project.id)
      return null
    } catch (cause) {
      return cause instanceof ApiError && cause.status === 409
        ? t('chat.sidebar.projectExists')
        : t('chat.sidebar.projectCreateFailed')
    }
  }

  const handleRename = useCallback(async (id: string, title: string) => {
    const updated = await renameConversation(id, title)
    setItems((current) => current.map((item) => (item.id === id ? updated : item)))
  }, [])

  const handleDelete = useCallback(async (id: string) => {
    await deleteConversation(id)
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  return (
    <section className="project-section" aria-label={t('chat.sidebar.projects')}>
      <div className="sidebar-label-row">
        <p className="sidebar-label">{t('chat.sidebar.projects')}</p>
        <button
          type="button"
          className="sidebar-label-action"
          onClick={() => setCreating((value) => !value)}
          aria-label={t('chat.sidebar.newProject')}
          title={t('chat.sidebar.newProject')}
          aria-expanded={creating}
        >
          <PlusIcon size={15} />
        </button>
      </div>
      {creating && <NewProjectInput onSubmit={create} onCancel={() => setCreating(false)} />}
      {deleting && (
        <DeleteProjectDialog
          project={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            // Acik proje silindiyse liste de kapaniyor: bos bir govdeyi
            // acik birakmak "sohbetler nerede" sorusunu dogururdu.
            if (openId === deleting.id) {
              setOpenId(null)
              setItems([])
            }
            setDeleting(null)
            void onCreated()
          }}
        />
      )}
      <ul className="conv-list">
        {projects.map((project) => {
          const isOpen = openId === project.id
          return (
            <li key={project.id} className="conv-item">
              <button
                type="button"
                className={isOpen ? 'conv project-row open' : 'conv project-row'}
                aria-expanded={isOpen}
                onClick={() => toggle(project.id)}
              >
                <FolderIcon size={15} />
                <span className="conv-title">{project.name}</span>
                <span className="project-count">
                  {t('chat.sidebar.projectCount', { count: project.conversation_count })}
                </span>
                <span className="project-chevron" aria-hidden="true">
                  <ChevronDownIcon size={14} />
                </span>
              </button>
              {/*
                Silme, satirin KARDESI - proje satiri zaten bir `<button>` ve
                icine ikinci bir buton koymak gecersiz HTML olurdu. Ikon,
                "Sil" yazili bir dugme degil: silme eylemleri uygulamanin
                her yerinde cop kutusu ikonuyla gosteriliyor.
              */}
              <button
                type="button"
                className="conv-menu-trigger project-delete"
                onClick={() => setDeleting(project)}
                aria-label={t('chat.sidebar.projectDelete', { name: project.name })}
                title={t('chat.sidebar.projectDelete', { name: project.name })}
              >
                <TrashIcon size={15} />
              </button>
              {isOpen && (
                <ul className="conv-list project-conversations">
                  <li>
                    <button
                      type="button"
                      className="project-more project-new-chat"
                      onClick={() => {
                        navigate(`/?project=${encodeURIComponent(project.id)}`)
                        onNavigate?.()
                      }}
                    >
                      <PlusIcon size={14} />
                      {t('chat.sidebar.newChatInProject')}
                    </button>
                  </li>
                  {items.map((conversation) => (
                    <ConvItem
                      key={conversation.id}
                      conversation={conversation}
                      active={conversation.id === conversationId}
                      generating={streamingIds.has(conversation.id)}
                      onNavigate={onNavigate}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      projects={projects}
                      onMove={onMove}
                    />
                  ))}
                  {loading && <li className="sidebar-note">{t('common.loading')}</li>}
                  {!loading && cursor !== null && (
                    <li>
                      <button
                        type="button"
                        className="project-more"
                        onClick={() => void load(project.id, cursor)}
                      >
                        {t('chat.sidebar.loadMore')}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
      {projects.length === 0 && !creating && (
        <p className="sidebar-note">{t('chat.sidebar.noProjects')}</p>
      )}
      <p className="sidebar-label">{t('chat.sidebar.chats')}</p>
    </section>
  )
}

/** Yeni proje adi: Enter olusturur, Escape vazgecer; hata satir altinda. */
function NewProjectInput({
  onSubmit,
  onCancel,
}: {
  onSubmit(name: string): Promise<string | null>
  onCancel(): void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit() {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    const problem = await onSubmit(trimmed)
    setBusy(false)
    setError(problem)
  }

  return (
    <div className="project-create">
      <input
        className="conv-rename-input"
        value={name}
        autoFocus
        maxLength={120}
        disabled={busy}
        placeholder={t('chat.sidebar.projectNamePlaceholder')}
        aria-label={t('chat.sidebar.projectName')}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          setName(event.target.value)
          setError(null)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            void submit()
          } else if (event.key === 'Escape') {
            event.preventDefault()
            onCancel()
          }
        }}
      />
      {error && (
        <p className="project-create-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function ConvItem({
  conversation,
  active,
  generating,
  onNavigate,
  onRename,
  onDelete,
  projects,
  onMove,
}: {
  conversation: Conversation
  active: boolean
  generating: boolean
  onNavigate?(): void
  onRename(id: string, title: string): Promise<void>
  onDelete(id: string): Promise<void>
  projects: Project[]
  onMove(id: string, projectId: string | null): Promise<void>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [choosingProject, setChoosingProject] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [title, setTitle] = useState(conversation.title)
  const menu = useRef<HTMLLIElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Escape ve blur ayni yeniden adlandirmayi iki kez gondermesin diye.
  const settled = useRef(true)

  useEffect(() => {
    if (!renaming) setTitle(conversation.title)
  }, [conversation.title, renaming])

  useEffect(() => {
    function close(event: MouseEvent) {
      if (menu.current && !menu.current.contains(event.target as Node)) {
        setOpen(false)
        setConfirmingDelete(false)
        setChoosingProject(false)
      }
    }
    if (open) document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  useEffect(() => {
    if (renaming) inputRef.current?.focus()
  }, [renaming])

  function startRename() {
    settled.current = false
    setTitle(conversation.title)
    setRenaming(true)
    setOpen(false)
  }

  async function submitRename() {
    if (settled.current) return
    settled.current = true
    const next = title.trim()
    setRenaming(false)
    if (!next || next === conversation.title) return
    try {
      await onRename(conversation.id, next)
    } catch {
      setTitle(conversation.title)
    }
  }

  function cancelRename() {
    settled.current = true
    setTitle(conversation.title)
    setRenaming(false)
  }

  async function submitMove(projectId: string | null) {
    setBusy(true)
    try {
      await onMove(conversation.id, projectId)
    } catch {
      // Basarisiz tasima: sohbet yerinde kaliyor.
    } finally {
      setBusy(false)
      setChoosingProject(false)
      setOpen(false)
    }
  }

  async function submitDelete() {
    setBusy(true)
    try {
      await onDelete(conversation.id)
    } finally {
      setBusy(false)
      setConfirmingDelete(false)
      setOpen(false)
    }
  }

  if (renaming) {
    return (
      <li>
        <input
          ref={inputRef}
          className="conv-rename-input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => void submitRename()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              inputRef.current?.blur()
            } else if (event.key === 'Escape') {
              event.preventDefault()
              cancelRename()
            }
          }}
          aria-label={t('chat.sidebar.renameLabel')}
        />
      </li>
    )
  }

  return (
    <li className="conv-item" ref={menu}>
      <Link
        to={`/c/${conversation.id}`}
        className={active ? 'conv active' : 'conv'}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
      >
        <span className="conv-title">{conversation.title || t('chat.untitled')}</span>
        {generating && (
          <span
            className="conv-dot"
            title={t('chat.sidebar.generating')}
            aria-label={t('chat.sidebar.generating')}
          />
        )}
      </Link>
      <button
        type="button"
        className="conv-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('chat.sidebar.convMenu')}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setOpen((value) => !value)
        }}
      >
        <MoreIcon size={16} />
      </button>
      {open && (
        <div className="conv-popover" role="menu">
          {choosingProject ? (
            <div className="conv-move" role="group" aria-label={t('chat.sidebar.moveToProject')}>
              <p className="conv-move-title">{t('chat.sidebar.moveToProject')}</p>
              <div className="conv-move-list">
                {projects
                  .filter((project) => project.id !== conversation.project_id)
                  .map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      className="conv-menu-item"
                      role="menuitem"
                      disabled={busy}
                      onClick={() => void submitMove(project.id)}
                    >
                      <FolderIcon size={16} />
                      <span className="conv-move-name">{project.name}</span>
                    </button>
                  ))}
              </div>
              {conversation.project_id && (
                <button
                  type="button"
                  className="conv-menu-item"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => void submitMove(null)}
                >
                  <CloseIcon size={16} />
                  {t('chat.sidebar.removeFromProject')}
                </button>
              )}
              {projects.every((project) => project.id === conversation.project_id) &&
                !conversation.project_id && (
                  <p className="conv-move-empty">{t('chat.sidebar.noProjectsToMove')}</p>
                )}
            </div>
          ) : !confirmingDelete ? (
            <>
              <button
                type="button"
                className="conv-menu-item"
                role="menuitem"
                onClick={() => {
                  setSharing(true)
                  setOpen(false)
                }}
              >
                <ShareIcon size={16} />
                {t('chat.share')}
              </button>
              <button
                type="button"
                className="conv-menu-item"
                role="menuitem"
                onClick={startRename}
              >
                <PencilIcon size={16} />
                {t('chat.sidebar.rename')}
              </button>
              <button
                type="button"
                className="conv-menu-item"
                role="menuitem"
                onClick={() => setChoosingProject(true)}
              >
                <FolderIcon size={16} />
                {t('chat.sidebar.moveToProject')}
              </button>
              <button
                type="button"
                className="conv-menu-item conv-menu-danger"
                role="menuitem"
                onClick={() => setConfirmingDelete(true)}
              >
                <TrashIcon size={16} />
                {t('chat.sidebar.delete')}
              </button>
            </>
          ) : (
            <div className="conv-menu-confirm">
              <span>{t('chat.sidebar.deleteConfirm')}</span>
              <div className="conv-menu-confirm-actions">
                <button
                  type="button"
                  className="conv-menu-confirm-btn conv-menu-danger"
                  onClick={() => void submitDelete()}
                  disabled={busy}
                  aria-label={t('chat.sidebar.deleteConfirmYes')}
                >
                  <CheckIcon size={14} />
                </button>
                <button
                  type="button"
                  className="conv-menu-confirm-btn"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                  aria-label={t('chat.sidebar.deleteConfirmNo')}
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {sharing && (
        <ShareDialog conversationId={conversation.id} onClose={() => setSharing(false)} />
      )}
    </li>
  )
}
